import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getCandidatures,
  deleteCandidature,
  importCandidatures,
  getStats,
  clearAuthSession,
  logoutAdmin,
  fetchMe,
} from './api';
import { toast } from './Toast';
import { logClientError } from './logClient';
import EditCandidatureModal from './EditCandidatureModal';
import CandidaturesPanel from './dashboard/CandidaturesPanel';
import ImportPanel from './dashboard/ImportPanel';
import StatsPanel from './dashboard/StatsPanel';
import {
  calculateStats,
  mergeStats,
  getDisponibiliteStats,
  getMoyenDeplacementAutreStats,
} from './dashboard/statsHelpers';

function Dashboard() {
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await fetchMe();
      } catch {
        if (!cancelled) {
          clearAuthSession();
          navigate('/admin/login');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const handleLogout = async () => {
    await logoutAdmin();
    navigate('/admin/login');
  };

  const [submissions, setSubmissions] = useState([]);
  const [filter, setFilter] = useState('all');
  const [activeMenu, setActiveMenu] = useState('candidatures');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [filterYear, setFilterYear] = useState('');
  const [filterQuartier, setFilterQuartier] = useState('');
  const [filterVille, setFilterVille] = useState('');
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [serverStats, setServerStats] = useState(null);
  const [editingSubmission, setEditingSubmission] = useState(null);

  useEffect(() => {
    const loadSubmissions = async () => {
      try {
        const data = await getCandidatures();
        setSubmissions(data);
      } catch (error) {
        logClientError('chargement candidatures', error);
        toast('Impossible de charger les candidatures', 'error');
      }
    };

    loadSubmissions();

    const interval = setInterval(loadSubmissions, 30000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (activeMenu !== 'statistiques') return undefined;
    let cancelled = false;
    (async () => {
      try {
        const data = await getStats();
        if (!cancelled) setServerStats(data);
      } catch (error) {
        logClientError('stats', error);
        if (!cancelled) {
          setServerStats(null);
          toast('Stats serveur indisponibles — calcul local', 'warning');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeMenu, submissions.length]);

  const deleteSubmission = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette candidature ?')) {
      try {
        await deleteCandidature(id);
        setSubmissions(submissions.filter(sub => sub._id !== id));
        if (editingSubmission?._id === id) {
          setEditingSubmission(null);
        }
        toast('Candidature supprimée', 'success');
      } catch (error) {
        logClientError('suppression candidature', error);
        toast('Erreur lors de la suppression', 'error');
      }
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const extension = file.name.split('.').pop().toLowerCase();
      if (extension !== 'csv' && extension !== 'json') {
        alert('Veuillez sélectionner un fichier CSV ou JSON');
        e.target.value = '';
        return;
      }
      setImportFile(file);
      setImportResult(null);
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      toast('Veuillez sélectionner un fichier à importer', 'warning');
      return;
    }

    if (
      !window.confirm(
        `Importer « ${importFile.name} » ? Des SMS de confirmation pourront être envoyés pour chaque nouvelle candidature.`
      )
    ) {
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const result = await importCandidatures(importFile);
      setImportResult(result);
      toast(
        `${result.imported || 0} importée(s)` +
          (result.smsQueued ? ` · ${result.smsQueued} SMS en file` : ''),
        result.imported > 0 ? 'success' : 'warning'
      );

      const data = await getCandidatures();
      setSubmissions(data);

      setImportFile(null);
      const input = document.querySelector('input[type="file"]');
      if (input) input.value = '';
    } catch (error) {
      setImportResult({
        message: 'Erreur lors de l\'import',
        imported: 0,
        errors: 1,
        total: 0,
        errorDetails: [error.message]
      });
      toast(error.message || 'Erreur lors de l\'import', 'error');
    } finally {
      setImporting(false);
    }
  };

  const getBirthYear = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.getFullYear();
  };

  const filteredSubmissions = submissions.filter(sub => {
    if (filter !== 'all') {
      const postes = sub.posteSouhaite;
      let posteMatch = false;
      if (filter === 'plomberie') posteMatch = postes?.plomberie;
      else if (filter === 'peinture') posteMatch = postes?.peinture;
      else if (filter === 'electricite') posteMatch = postes?.electricite;
      else if (filter === 'froidClimatisation') posteMatch = postes?.froidClimatisation;
      if (!posteMatch) return false;
    }

    if (filterYear) {
      const birthYear = getBirthYear(sub.dateNaissance);
      if (birthYear !== parseInt(filterYear)) return false;
    }

    if (filterVille) {
      if (!sub.ville || sub.ville.toLowerCase() !== filterVille.toLowerCase()) {
        return false;
      }
    }

    if (filterQuartier) {
      if (!sub.quartier || sub.quartier.toLowerCase() !== filterQuartier.toLowerCase()) {
        return false;
      }
    }

    return true;
  });

  const localStats = calculateStats(submissions);
  const stats = mergeStats(localStats, serverStats);
  const disponibiliteStats = getDisponibiliteStats(submissions);
  const moyenDeplacementAutreStats = getMoyenDeplacementAutreStats(submissions);

  return (
    <>
    <div className="min-h-screen bg-gradient-to-br from-blue-100 via-blue-200 to-blue-100 flex">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-gradient-to-b from-blue-50 to-white shadow-xl transition-all duration-300 flex flex-col border-r border-gray-200`}>
        {/* Header */}
        <div className="p-4 border-b border-gray-200 bg-white">
          <div className="flex items-center justify-between">

            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors ml-auto"
              title={sidebarOpen ? "Réduire" : "Agrandir"}
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {sidebarOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          <button
            onClick={() => setActiveMenu('candidatures')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
              activeMenu === 'candidatures'
                ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg transform scale-105'
                : 'text-gray-700 hover:bg-blue-50 hover:text-blue-600'
            }`}
            title="Candidatures"
          >
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            {sidebarOpen && <span className="font-medium">Candidatures</span>}
          </button>

          <button
            onClick={() => setActiveMenu('statistiques')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
              activeMenu === 'statistiques'
                ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg transform scale-105'
                : 'text-gray-700 hover:bg-blue-50 hover:text-blue-600'
            }`}
            title="Statistiques"
          >
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            {sidebarOpen && <span className="font-medium">Statistiques</span>}
          </button>

          <button
            onClick={() => setActiveMenu('import')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
              activeMenu === 'import'
                ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg transform scale-105'
                : 'text-gray-700 hover:bg-blue-50 hover:text-blue-600'
            }`}
            title="Import"
          >
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            {sidebarOpen && <span className="font-medium">Import</span>}
          </button>
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-white">
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-md hover:shadow-lg"
            title="Déconnexion"
          >
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            {sidebarOpen && <span className="font-medium">Déconnexion</span>}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {/* Banner Header */}
        <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 rounded-lg shadow-lg mb-4 p-4 text-white relative overflow-hidden">
          {/* Pattern overlay */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}></div>
          </div>

          {/* Content */}
          <div className="relative z-10">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-3">
              <div>
                <h1 className="text-xl md:text-2xl font-bold mb-1">Dashboard Administrateur</h1>
                <p className="text-blue-100 text-xs md:text-sm">Gestion des candidatures et statistiques</p>
              </div>
              <div className="mt-2 md:mt-0 flex items-center gap-2">
                <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg px-3 py-1.5">
                  <div className="text-xs text-blue-100">Total</div>
                  <div className="text-lg font-bold">{stats.total}</div>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              <div className="bg-white bg-opacity-15 backdrop-blur-sm rounded-lg p-2 border border-white border-opacity-20">
                <div className="text-xs text-blue-100 mb-0.5">Plomberie</div>
                <div className="text-lg font-bold">{stats.plomberie}</div>
              </div>
              <div className="bg-white bg-opacity-15 backdrop-blur-sm rounded-lg p-2 border border-white border-opacity-20">
                <div className="text-xs text-blue-100 mb-0.5">Peinture</div>
                <div className="text-lg font-bold">{stats.peinture}</div>
              </div>
              <div className="bg-white bg-opacity-15 backdrop-blur-sm rounded-lg p-2 border border-white border-opacity-20">
                <div className="text-xs text-blue-100 mb-0.5">Électricité</div>
                <div className="text-lg font-bold">{stats.electricite}</div>
              </div>
              <div className="bg-white bg-opacity-15 backdrop-blur-sm rounded-lg p-2 border border-white border-opacity-20">
                <div className="text-xs text-blue-100 mb-0.5">Froid/Climatisation</div>
                <div className="text-lg font-bold">{stats.froidClimatisation}</div>
              </div>
              <div className="bg-white bg-opacity-15 backdrop-blur-sm rounded-lg p-2 border border-white border-opacity-20">
                <div className="text-xs text-blue-100 mb-0.5">Autre</div>
                <div className="text-lg font-bold">{stats.autre}</div>
              </div>
            </div>
          </div>
        </div>

        {activeMenu === 'candidatures' && (
          <CandidaturesPanel
            submissions={submissions}
            filteredSubmissions={filteredSubmissions}
            filter={filter}
            setFilter={setFilter}
            filterYear={filterYear}
            setFilterYear={setFilterYear}
            filterVille={filterVille}
            setFilterVille={setFilterVille}
            filterQuartier={filterQuartier}
            setFilterQuartier={setFilterQuartier}
            onEdit={setEditingSubmission}
            onDelete={deleteSubmission}
          />
        )}

        {activeMenu === 'import' && (
          <ImportPanel
            importFile={importFile}
            importing={importing}
            importResult={importResult}
            onFileChange={handleFileChange}
            onImport={handleImport}
          />
        )}

        {activeMenu === 'statistiques' && (
          <StatsPanel
            stats={stats}
            submissions={submissions}
            disponibiliteStats={disponibiliteStats}
            moyenDeplacementAutreStats={moyenDeplacementAutreStats}
          />
        )}
      </div>
    </div>

    {editingSubmission && (
      <EditCandidatureModal
        submission={editingSubmission}
        onClose={() => setEditingSubmission(null)}
        onSaved={(updated) => {
          setSubmissions((prev) =>
            prev.map((s) => (s._id === updated._id ? updated : s))
          );
        }}
      />
    )}
    </>
  );
}

export default Dashboard;
